import { Box, Card, Autocomplete, Chip, Accordion, AccordionDetails, AccordionSummary, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, LinearProgress, Switch, TextField, Typography, Avatar, Badge, IconButton, Icon, useTheme } from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FormHandles } from '@unform/core';
import { Form } from '../../../shared/forms';
import { MuiColorInput } from 'mui-color-input';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as Yup from 'yup';
import { ICreateBusinessAdmin, ICreateEmployee, ICreateBusinessEmployee, IBusinessEmployeeService, IBusinessEmployeeServiceView, IUpdateBusinessEmployee, businessAdminService, businessEmployeeService, employeeService, finishRequest, feedback, commissionGoalService } from '../../../shared/services';
import { IPropsBusinessPage } from '../Business';
import { useUserContext } from '../../../shared/contexts';
import getValidationErrors from '../../../shared/helpers/getValidationErrors';
import { LayoutRegister } from '../../../shared/layouts';
import { VTextField, VTextFieldPercentage } from '../../../shared/forms';
import { Button, DayScheduleEditor, IDaySchedule, weeklySchedulesToDays, scheduleToApiDto, validateScheduleDays, FileUploader } from '../../../shared/components';
import { BusinessEmployeeService } from '../../../shared/components/SconApp/businessEmployeeService/BusinessEmployeeService';

const schema = Yup.object().shape({
  name: Yup.string().required()
});

const commissionRateSchema = Yup.object().shape({
  servicesCommissionRate: Yup.number().required('Comissão é obrigatória').min(0, 'Mínimo 0%').max(100, 'Máximo 100%')
});

interface IProps extends IPropsBusinessPage {
  businessEmployeeId?: number;
  isCreateBusinessAdmin: boolean;
  onCloseEdit: () => void;
}

export const BusinessEmployeesRegister: React.FC<IProps> = ({ business, refetch, onCloseEdit, businessEmployeeId, isCreateBusinessAdmin }) => {
  const [employeeServices, setEmployeeServices] = useState<{ [key: number]: IBusinessEmployeeServiceView[] }>({ [business.id]: [] });
  const [schedule, setSchedule] = useState<IDaySchedule[]>(() =>
    weeklySchedulesToDays(business.weeklySchedules)
  );
  const [isActive, setIsActive] = useState(true);
  const [isReceptionist, setIsReceptionist] = useState(false);
  const [selectedGoalIds, setSelectedGoalIds] = useState<number[]>([]);
  const [confirmNoServices, setConfirmNoServices] = useState(false);
  // Ref porque formRef.current.submitForm() chama o handler síncrono,
  // então um state não teria atualizado a tempo de pular o check na re-submissão.
  const confirmedNoServices = useRef(false);
  const [confirmScheduleClamp, setConfirmScheduleClamp] = useState(false);
  const [scheduleClampMessages, setScheduleClampMessages] = useState<string[]>([]);
  const confirmedScheduleClamp = useRef(false);
  const pendingClampedSchedule = useRef<IDaySchedule[]>([]);
  const [profilePicture, setProfilePicture] = useState<string | undefined>();
  const [openDialogUploadFile, setOpenDialogUploadFile] = useState(false);

  const theme = useTheme();
  const [color, setColor] = useState<string>(theme.palette.primary.main);

  const { userId, userName } = useUserContext();

  const formRef = useRef<FormHandles>(null);
  const queryClient = useQueryClient();

  const { data: businessEmployee, isLoading: isLoadingEmployee } = useQuery({
    queryKey: ['businessEmployee', businessEmployeeId],
    queryFn: () => {
      if (businessEmployeeId) {
        return businessEmployeeService.getById(businessEmployeeId);
      }
    },
    enabled: !!businessEmployeeId,
  });

  // Sync schedule when employee data loads (guard against null/empty schedules)
  useEffect(() => {
    if (businessEmployee?.weeklySchedules && businessEmployee.weeklySchedules.length > 0) {
      setSchedule(weeklySchedulesToDays(businessEmployee.weeklySchedules));
    }
  }, [businessEmployee]);

  const { data: availableGoals } = useQuery({
    queryKey: ['goals', business.id],
    queryFn: () => commissionGoalService.getCommissionGoals(business.id),
  });

  const { data: employeeGoals } = useQuery({
    queryKey: ['employeeGoals', businessEmployeeId],
    queryFn: () => businessEmployeeId ? commissionGoalService.getEmployeeGoals(businessEmployeeId) : Promise.resolve([]),
    enabled: !!businessEmployeeId,
  });

  useEffect(() => {
    if (businessEmployee) {
      formRef.current?.setData(businessEmployee);
      setEmployeeServices((prevState) => ({
        ...prevState,
        [business.id]: businessEmployee.businessEmployeeServices || [],
      }));
      setColor(businessEmployee.color);
      setIsActive(businessEmployee.isActive);
      setIsReceptionist(businessEmployee.isReceptionist);
      setProfilePicture(businessEmployee.profilePicture);
    }
  }, [businessEmployee]);

  useEffect(() => {
    if (employeeGoals) {
      setSelectedGoalIds(employeeGoals.map(g => g.id));
    }
  }, [employeeGoals]);

  const businessEmployeeServices = useMemo(() => employeeServices && employeeServices[business.id].map(item => (
    {
      businessEmployeeId: item.businessEmployeeId,
      serviceId: item.serviceId,
      serviceDuration: item.serviceDuration
    }) as IBusinessEmployeeService
  ), [employeeServices]);

  const { mutate: createEmployee, isLoading: isLoadingCreateEmployee } = useMutation((employee: ICreateEmployee) =>
    employeeService.create(employee), {
    onSuccess: () => finishRequest('Profissional criado', 'success', () => {
      refetch();
      onCloseEdit();
    }),
    onError: (error: Error) => finishRequest(error.message, 'error'),
  });

  const { mutate: createBusinessEmployee, isLoading: isLoadingCreateBusinessEmployee } = useMutation((data: ICreateBusinessEmployee) =>
    businessEmployeeService.create(data), {
    onSuccess: () => finishRequest('Profissional criado', 'success', () => {
      refetch();
      onCloseEdit();
    }),
    onError: (error: Error) => finishRequest(error.message, 'error'),
  });

  const { mutate: updateBusinessEmployee, isLoading: isLoadingUpdateBusinessEmployee } = useMutation((data: IUpdateBusinessEmployee) =>
    businessEmployeeService.update(data), {
    onSuccess: () => {
      queryClient.invalidateQueries(['businessEmployee', businessEmployeeId]);
      finishRequest('Profissional editado', 'success', () => {
        refetch();
        onCloseEdit();
      });
    },
    onError: (error: Error) => finishRequest(error.message, 'error'),
  });

  const { mutate: createBusinessAdmin, isLoading: isLoadingCreateBusinessAdmin } = useMutation((businessAdmin: ICreateBusinessAdmin) =>
    businessAdminService.create(businessAdmin), {
    onSuccess: () => finishRequest('Profissional agora é administrador.', 'success', () => {
      refetch();
      onCloseEdit();
    }),
    onError: (error: Error) => finishRequest(error.message, 'error'),
  });

  const onClickCreateBusinessAdmin = useCallback(() => {
    if (businessEmployee?.employee) {
      const newBusinessAdmin: ICreateBusinessAdmin = {
        userId: businessEmployee?.employee?.userId,
        businessId: business.id
      };
      createBusinessAdmin(newBusinessAdmin);
    }
  }, [businessEmployee]);

  const handleSubmit = useCallback(async (data: Record<string, unknown>) => {
    if (businessEmployeeServices.length === 0 && !confirmedNoServices.current) {
      setConfirmNoServices(true);
      return;
    }
    confirmedNoServices.current = false;

    try {
      formRef.current?.setErrors({});

      if (!businessEmployee?.employeeId && !isCreateBusinessAdmin) {
        await schema.validate({ name: data.name }, { abortEarly: false });
      }

      await commissionRateSchema.validate({ servicesCommissionRate: data.servicesCommissionRate }, { abortEarly: false });

      // Save goals before employee update, await to ensure persistence
      if (businessEmployeeId && selectedGoalIds.length >= 0) {
        await commissionGoalService.setEmployeeGoals(businessEmployeeId, selectedGoalIds);
        queryClient.invalidateQueries(['employeeGoals', businessEmployeeId]);
      }

      // ── Clamp employee hours to business hours ──
      // Skip clamping if user just confirmed the clamp dialog (values already clamped)
      if (confirmedScheduleClamp.current) {
        confirmedScheduleClamp.current = false;
      } else {
        const businessSchedules = business.weeklySchedules || [];
        const clampedSchedule = schedule.map(day => {
          const clamped = { ...day };
          const bizDay = businessSchedules.find(s => s.dayOfWeek === day.dayOfWeek);
          if (clamped.isClosed || !bizDay || bizDay.isClosed) return clamped;

          if (clamped.startTime && bizDay.startTime && clamped.startTime < bizDay.startTime)
            clamped.startTime = bizDay.startTime;
          if (clamped.endTime && bizDay.endTime && clamped.endTime > bizDay.endTime)
            clamped.endTime = bizDay.endTime;

          return clamped;
        });

        const adjustments: string[] = [];
        const dayNames = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
        for (let i = 0; i < schedule.length; i++) {
          if (schedule[i].isClosed) continue;
          const bizDay = businessSchedules.find(s => s.dayOfWeek === schedule[i].dayOfWeek);
          if (!bizDay || bizDay.isClosed) continue;
          if (schedule[i].startTime !== clampedSchedule[i].startTime) {
            adjustments.push(
              `${dayNames[schedule[i].dayOfWeek]}: início ajustado de ${schedule[i].startTime} para ${clampedSchedule[i].startTime}`
            );
          }
          if (schedule[i].endTime !== clampedSchedule[i].endTime) {
            adjustments.push(
              `${dayNames[schedule[i].dayOfWeek]}: término ajustado de ${schedule[i].endTime} para ${clampedSchedule[i].endTime}`
            );
          }
        }

        if (adjustments.length > 0) {
          pendingClampedSchedule.current = clampedSchedule;
          setScheduleClampMessages(adjustments);
          setConfirmScheduleClamp(true);
          return;
        }
      }

      // Validate start < end per day
      const scheduleErr = validateScheduleDays(schedule);
      if (scheduleErr) { feedback(scheduleErr, 'error'); return; }

      const scheduleDto = scheduleToApiDto(schedule);

      if (businessEmployee) {
        const weeklySchedules = scheduleToApiDto(schedule);

        const submitData: IUpdateBusinessEmployee = {
          id: businessEmployee.id,
          name: data.name as string | undefined,
          employeeId: businessEmployee.employeeId,
          businessId: businessEmployee.businessId,
          servicesCommissionRate: data.servicesCommissionRate as number,
          color: color,
          isActive,
          isReceptionist,
          profilePicture: profilePicture,
          businessEmployeeServices: businessEmployeeServices,
          weeklySchedules,
        };

        updateBusinessEmployee(submitData);
        return;
      }

      const submitData: ICreateBusinessEmployee = {
        businessId: business.id,
        name: data.name as string | undefined,
        servicesCommissionRate: data.servicesCommissionRate as number,
        color: color,
        isActive,
        isReceptionist,
        profilePicture: profilePicture,
        businessEmployeeServices: businessEmployeeServices,
        weeklySchedules: scheduleDto,
      };

      if (isCreateBusinessAdmin) {
        const employee: ICreateEmployee = {
          userId: userId,
          businessEmployee: submitData
        };
        createEmployee(employee);
        return;
      }

      createBusinessEmployee(submitData);
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const errors = getValidationErrors(err as Yup.ValidationError);
        console.log(errors);
        formRef.current?.setErrors(errors);
      }
    }
  }, [businessEmployee, businessEmployeeServices, schedule, color, profilePicture, business.weeklySchedules, selectedGoalIds, isActive, isReceptionist]);

  const handleConfirmNoServices = useCallback(() => {
    confirmedNoServices.current = true;
    setConfirmNoServices(false);
    // Re-trigger submit; the ref will skip the check this time
    formRef.current?.submitForm();
  }, []);

  const handleCancelNoServices = useCallback(() => {
    setConfirmNoServices(false);
  }, []);

  const handleConfirmScheduleClamp = useCallback(() => {
    setSchedule(pendingClampedSchedule.current);
    confirmedScheduleClamp.current = true;
    setConfirmScheduleClamp(false);
    // Re-trigger submit with the clamped schedule
    formRef.current?.submitForm();
  }, []);

  const handleCancelScheduleClamp = useCallback(() => {
    setConfirmScheduleClamp(false);
  }, []);

  const onClose = () => {
    setOpenDialogUploadFile(false);
  };

  const onDelete = () => {
    setProfilePicture(undefined);
  };

  const name = useMemo(() => businessEmployee?.name || userName, [businessEmployee, userName]);

  const isRegularEmployee = useMemo(() => businessEmployee?.employee && !business.admins.map(x => x.userId).includes(businessEmployee.employee.userId), [businessEmployee]);

  return !isLoadingEmployee || !businessEmployeeId ? (
    <Box>
      <Form ref={formRef} onSubmit={handleSubmit}>
        <LayoutRegister header={'Profissional'} isSubmit isLoading={isLoadingCreateBusinessEmployee || isLoadingUpdateBusinessEmployee || isLoadingCreateEmployee}>
          <Box display='flex' flexDirection='column' gap={2}>
            {/* Profile picture — upload only for non-SconApp employees */}
            <Box display='flex' justifyContent='center'>
              <Badge
                overlap="circular"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                badgeContent={
                  businessEmployee && !businessEmployee.employeeId ? (
                    <IconButton onClick={() => setOpenDialogUploadFile(true)}>
                      <Icon>photo_camera</Icon>
                    </IconButton>
                  ) : undefined
                }
              >
                <Avatar src={profilePicture} sx={{ height: 120, width: 120 }} />
              </Badge>
            </Box>

            {isCreateBusinessAdmin || businessEmployee?.employeeId ?
              <TextField value={name} disabled />
              : <VTextField name='name' label='Nome profissional' required />
            }

            {schedule.length > 0 && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography>Horários de Atendimento</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <DayScheduleEditor
                    schedule={schedule}
                    onChange={setSchedule}
                  />
                </AccordionDetails>
              </Accordion>
            )}

            <VTextFieldPercentage
              label='Comissão sobre serviços (%)'
              name='servicesCommissionRate'
              required
            />

            <MuiColorInput label='Cor do profissional' format="hex" value={color} onChange={(value) => setColor(value)} />

            <FormControlLabel
              control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
              label={isActive ? 'Ativo' : 'Inativo'}
            />

            {businessEmployee?.employeeId && isRegularEmployee && (
              <FormControlLabel
                control={<Switch checked={isReceptionist} onChange={(e) => setIsReceptionist(e.target.checked)} />}
                label='Recepcionista'
              />
            )}

            <BusinessEmployeeService
              businessEmployeeId={businessEmployeeId}
              businessId={business.id}
              services={business.services}
              employeeServices={employeeServices} 
              setEmployeeServices={setEmployeeServices}
            />

            {/* Show goals section — Autocomplete multiselect */}
            {availableGoals && availableGoals.length > 0 && (
              <Box>
                <Typography variant='subtitle2' gutterBottom>Metas</Typography>
                <Autocomplete
                  multiple
                  options={availableGoals}
                  getOptionLabel={(goal) => `${goal.name} — ${goal.periodDays}d — ${goal.tiers.length} tier(s)`}
                  value={availableGoals.filter(g => selectedGoalIds.includes(g.id))}
                  onChange={(_, newValue) => setSelectedGoalIds(newValue.map(g => g.id))}
                  renderInput={(params) => (
                    <TextField {...params} variant='outlined' placeholder='Selecionar metas...' />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        size='small'
                        label={option.name}
                        {...getTagProps({ index })}
                        key={option.id}
                      />
                    ))
                  }
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  noOptionsText='Nenhuma meta disponível'
                />
              </Box>
            )}

            {isRegularEmployee &&
              <Button
                variant='contained'
                label='Tornar administrador'
                onClick={onClickCreateBusinessAdmin}
                isLoading={isLoadingCreateBusinessAdmin}
                disabled={isLoadingCreateBusinessAdmin}
              />}
          </Box>
        </LayoutRegister>
      </Form>

      <Dialog open={confirmNoServices} onClose={handleCancelNoServices}>
        <DialogTitle>Atenção</DialogTitle>
        <DialogContent>
          <Typography>
            Nenhum serviço vinculado a este profissional. Sem serviços vinculados, ele não aparecerá nas buscas dos clientes. Tem certeza que deseja continuar?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button label='Cancelar' variant='outlined' onClick={handleCancelNoServices} />
          <Button label='Sim, tenho certeza' variant='contained' onClick={handleConfirmNoServices} />
        </DialogActions>
      </Dialog>

      <Dialog open={confirmScheduleClamp} onClose={handleCancelScheduleClamp}>
        <DialogTitle>Horários ajustados</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>
            Os horários do profissional serão ajustados para ficar dentro do horário de funcionamento do estabelecimento:
          </Typography>
          {scheduleClampMessages.map((msg, i) => (
            <Typography key={i} variant='body2' sx={{ ml: 2 }}>
              • {msg}
            </Typography>
          ))}
        </DialogContent>
        <DialogActions>
          <Button label='Cancelar' variant='outlined' onClick={handleCancelScheduleClamp} />
          <Button label='Confirmar' variant='contained' onClick={handleConfirmScheduleClamp} />
        </DialogActions>
      </Dialog>

      {!!businessEmployeeId && (
        <FileUploader
          open={openDialogUploadFile}
          id={businessEmployeeId}
          refetch={refetch}
          uploadFile={businessEmployeeService.uploadFile}
          deleteFile={businessEmployeeService.deleteFile}
          onDelete={onDelete}
          onClose={onClose}
          onUploadSuccess={(url) => setProfilePicture(url)}
        />
      )}
    </Box>
  ) : (
    <Card sx={{ width: 400, height: 200 }}>
      <LinearProgress />
    </Card>
  );
};
